import { createReducer, on } from '@ngrx/store';
import * as ProductsActions from '../actions/products.actions';

export const productsFeatureKey = 'products';

export interface State {
  currentPage: number,
  totalPage: number,
  lastSavedProductId: number,
  products: any[],
  productsCount: any,
  filterType: string,
  filterID: number
  total: number,
  query: string,
  prdStorage: any,
  prdCatID: number,
  prdIsAsset: any,
  isLoading: boolean,
  sortType: string,
  direction: string,
  product: any,
  selected: any,
  isMasterChecked: boolean,
  barcodes: any[],
  displayedColumns: string[];
  allColumns: { name: string; displayName: string, order:number }[];

  /**
   * Errors
   */
  duplicateBarcode: boolean
}

export const initialState: State = {
  currentPage: 0,
  totalPage: 0,
  lastSavedProductId: 0,
  products: null,
  productsCount: 0,
  product: null,
  filterType: null,
  total: 0,
  filterID: 0,
  query: "",
  prdStorage: null,
  prdCatID: 0,
  prdIsAsset: null,
  isLoading: true,
  sortType: null,
  direction: null,
  selected: [],
  isMasterChecked: false,
  barcodes: null,
  duplicateBarcode: false,
  displayedColumns: ['checkbox','prdNameEn', 'prdCode', 'prdTotalOnHand', 'prdLastCounted', 'prdDelete'], 
  allColumns: [
    { name: 'checkbox', displayName: '', order: 1 },
    { name: 'prdNameEn', displayName: 'products.table.prdNameEn', order: 2 },
    { name: 'prdDescription', displayName: 'productDescription', order: 3 },
    { name: 'barcodeData', displayName: 'products.table.barcodes', order: 4 },
    { name: 'prdCode', displayName: 'products.table.prdCode', order: 5 },
    // The two custom fields carry a per-location name (Size, Colour...), so
    // their heading comes from settings rather than a translation key. They
    // are hidden from the picker on locations that don't name them.
    { name: 'prdCustomField1', displayName: '', order: 6 },
    { name: 'prdCustomField2', displayName: '', order: 7 },
    { name: 'prdTotalOnHand', displayName: 'products.table.totalOnHand', order: 8 },
    { name: 'prdLastCounted', displayName: 'products.table.lastCounted', order: 9 },
    { name: 'storages_count', displayName: 'products.table.storages', order: 10 },
    { name: 'prdDelete', displayName: '', order: 11 }
  ]
};

export const reducer = createReducer(
  initialState,

  on(ProductsActions.loadProducts, (state, action: any) => ({
    ...state,
    query: action?.query !== undefined ? action.query : state.query,
    prdStorage: action?.prdStorage !== undefined ? action.prdStorage : state.prdStorage,
    prdCatID: action?.prdCatID !== undefined ? action.prdCatID : state.prdCatID,
    prdIsAsset: action?.prdIsAsset !== undefined ? action.prdIsAsset : state.prdIsAsset
  })),
  on(ProductsActions.loadProductsSuccess, (state, action) => ({
    ...state,
    products: [...action.products],
    productsCount: action.products.length,
    total: action.total,
    totalPage : Math.ceil(action.total / 100),
    isLoading: false
  })),
  on(ProductsActions.loadProductsFailure, (state, action) => state),
  on(ProductsActions.loadProduct, state => state),
  on(ProductsActions.loadProductSuccess, (state, action) => ({
    ...state,
    product: action.product,
    isLoading: false
  })),
  on(ProductsActions.updateProduct, state => state),
  on(ProductsActions.updateProductSuccess, (state, { product }) => ({
    ...state,
    products: state.products?.map(item => {
      if(item.prdID == product.prdID){
        // Merge: the update response lacks list-only fields (movement and
        // storage counts behind the activity icon), which used to vanish
        // from the row until the page was reloaded.
        return { ...item, ...product };
      }else{
        return item
      }
    }),
    product: product,
    isLoading: false
  })),
  on(ProductsActions.patchProductInList, (state, { prdID, changes }) => ({
    ...state,
    products: state.products?.map(item => item.prdID == prdID ? { ...item, ...changes } : item),
  })),
  on(ProductsActions.loadProductFailure, (state, action) => state),
  on(ProductsActions.filterByCategory, (state, action )=> ({
    ...state,
    sortType: action.active,
    direction: action.direction,
    isLoading: true
  })),
  on(ProductsActions.filterByCategorySuccess, (state, action) => ({
    ...state,
    products: action.products,
    productsCount: action.products.length,
    total: action.total,
    totalPage : Math.ceil(action.total / 100),
    isLoading: false
  })),
  on(ProductsActions.filterByCategoryFailure, (state, action) => ({
    ...state,
    error: action.error,
  })),
  on(ProductsActions.filterToggleStorage, (state, action )=> ({
    ...state,
    sortType: action.active,
    direction: action.direction,
    isLoading: true
  })),
  on(ProductsActions.filterToggleStorageSuccess, (state, action) => ({
    ...state,
    products: action.products,
    productsCount: action.products.length,
    total: action.total,
    totalPage : Math.ceil(action.total / 100),
    isLoading: false
  })),
  on(ProductsActions.filterToggleStorageFailure, (state, action) => ({
    ...state,
    error: action.error,
  })),
  on(ProductsActions.filterByArea, (state, action )=> ({
    ...state,
    sortType: action.active,
    direction: action.direction,
    isLoading: true
  })),
  on(ProductsActions.filterByAreaSuccess, (state, action) => ({
    ...state,
    products: action.products,
    productsCount: state.products.length,
    total: action.total,
    totalPage : Math.ceil(action.total / 100),
    isLoading: false
  })),
  on(ProductsActions.filterByAreaFailure, (state, action) => ({
    ...state,
    error: action.error,
  })),
  on(ProductsActions.filterByStorages, (state, action )=> ({
    ...state,
    sortType: action.active,
    direction: action.direction,
    isLoading: true
  })),
  on(ProductsActions.filterByStoragesSuccess, (state, action) => ({
    ...state,
    products: action.products,
    productsCount: state.products.length,
    total: action.total,
    totalPage : Math.ceil(action.total / 100),
    isLoading: false
  })),
  on(ProductsActions.filterByStoragesFailure, (state, action) => ({
    ...state,
    error: action.error,
  })),
  on(ProductsActions.deleteProduct, (state, action) => ({
    ...state,
  })),
  on(ProductsActions.deleteProductSuccess, (state, action) => ({
    ...state,
    products: state.products.filter(product => product.prdID != action.prdID),
    query: action.query,
    productsCount: state.products.length,
  })),
  on(ProductsActions.deleteProductsSuccess, (state, action) => ({
    ...state,
    query: action.query,
    isMasterChecked: false
  })),
  on(ProductsActions.saveProduct, state => state),
  on(ProductsActions.saveProductSuccess, (state, action) => ({
    ...state,
    products: [
      action.product,
      ...(state.products || []),
    ],
    productsCount: (state.products?.length || 0) + 1,
    total: state.total +  1,
    lastSavedProductId: action.product.prdID,
    totalPage : Math.ceil(state.total / 100),
  })),
  on(ProductsActions.saveProductFailure, (state, action) => state),

  on(ProductsActions.search, (state, action) => ({
    ...state,
    query: action.query,
    isLoading: true
  })),
  on(ProductsActions.searchSuccess, (state, action) => ({
    ...state,
    products: action.products,
    productsCount: action.products.length,
    total: action.total,
    totalPage : Math.ceil(action.total / 100),
    isLoading: false
  })),
  on(ProductsActions.searchFailure, (state, action) => ({
    ...state,
    error: action.error,
  })),
  on(ProductsActions.incrementPage, (state, action) => ({ 
    ...state,
    currentPage: state.currentPage + 1
  })),
  on(ProductsActions.decrementPage, (state, action) => ({ 
    ...state,
    currentPage: state.currentPage - 1
  })),
  on(ProductsActions.resetPage, (state, action) => ({ 
    ...state,
    currentPage: 0,
    query: ""
  })),
  on(ProductsActions.incrementPage, (state, action) => ({ 
    ...state,
  })),
  on(ProductsActions.incrementPageSuccess, (state, action) => ({ 
    ...state,
    currentPage: action.currentPage
  })),
  on(ProductsActions.setFilterType, (state, action) => ({
    ...state,
    filterType: action.filterType,
    filterID: action.filterID
  })),
  on(ProductsActions.setSelected, (state, action) => ({
    ...state,
    selected: action.selected,
    isMasterChecked: true
  })),
  on(ProductsActions.setDeselected, (state) => ({
    ...state,
    selected: [],
    isMasterChecked: false
  })),
  on(ProductsActions.setSort, (state, action) => ({
    ...state,
    sortType: action.sort,
    direction: action.direction
  })),
  on(ProductsActions.resetProduct, (state, action) => ({
    ...state,
    product: null
  })),
  on(ProductsActions.resetProducts, (state, action) => ({
    ...state,
    products: [],
    productsCount: 0,
    isLoading: true
  })),
  on(ProductsActions.resetFilter, (state, action) => ({
    ...state,
    filterType: null,
    filterID: null
  })),
  on(ProductsActions.createBarcode, (state, action) => ({
    ...state,
    barcodes: [...state.barcodes, {
      barcodeData: action.barcodeData
    }],
    duplicateBarcode: false
  })),
  on(ProductsActions.setBarcodes, (state, action) => ({
    ...state,
    barcodes: action.barcodes
  })),
  on(ProductsActions.createBarcodeFailure, (state, action) => ({
    ...state,
    duplicateBarcode: true
  })),
  on(ProductsActions.createBarcodeFailure, (state, action) => ({
    ...state,
    duplicateBarcode: true
  })),
  on(ProductsActions.setSelectedBarcode, (state, action) => ({
    ...state,
    barcodes: state.barcodes?.map((item, index) => {
      if(index != action.index){
        item.selected = false;
      }else{
        item.selected = true;
      }
      return item;
    })
  })),
  on(ProductsActions.onOverride, (state, action) => ({
    ...state,
    products: state.products.map(item => 
      item.prdID === action.prdID 
        ? { ...item, prdTotalOnHand: action.onHand } 
        : item
    ),
    product: state.product && state.product.prdID === action.prdID 
      ? { 
          ...state.product, 
          prdTotalOnHand: action.onHand,
          isOnHandUpdated: true
        }
      : state.product,
    
  })),
  on(ProductsActions.addProductStoragesSuccess, (state, action) => ({
    ...state,
    products: state.products.map(item => {

      const updatedProduct = action.product.products.find(p => p.prdID === item.prdID);
    
      if (updatedProduct) {
          item.prdTotalOnHand = updatedProduct.prdTotalOnHand;
          item.movement_count = updatedProduct.movement_count;
          item.storages_count = updatedProduct.storages_count;
      }
      return item;
    }),
  })),
  on(ProductsActions.deleteBarcode, (state, action) => ({
    ...state,
    barcodes: state.barcodes.filter(item => item.barcodeID != action.barcodeID)
  })),
  on(ProductsActions.setIsLoading, (state, action) => ({
    ...state,
    isLoading: action.isLoading
  })),
  on(ProductsActions.updateDisplayedColumns, (state, { displayedColumns }) => ({
    ...state,
    displayedColumns,
  }))

);
